<template>
  <AppHeaderBack title="Delete Account" />
  <div class="min-h-screen bg-blue-50">
    <!-- Header section with UP colors -->
    <div class="bg-gradient-to-r from-blue-900 to-blue-700 py-8 shadow-md">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col items-center">
          <div class="text-center">
            <h1 class="text-2xl font-bold text-white mb-1">Delete Account</h1>
            <p class="text-blue-100">This action is permanent and cannot be undone.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Main content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div class="grid grid-cols-1 max-w-md mx-auto">
        <div class="bg-white rounded-lg shadow transform transition-transform duration-300 hover:-translate-y-1 hover:shadow-md">
          <div class="p-6">
            <!-- Initial Delete Button -->
            <div v-if="!showDeleteInput" class="text-center">
              <p class="text-gray-700 mb-6">
                This will permanently delete your account and all associated data.<br>
                <span class="text-red-600 font-semibold">This action cannot be undone.</span>
              </p>
              <button
                @click="showDeleteInput = true"
                class="w-full flex justify-center items-center py-3 px-4 rounded-lg font-semibold text-white bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 transition"
              >
                <span class="material-symbols-outlined mr-2">delete</span>
                Delete Account
              </button>
            </div>

            <!-- Confirmation Form -->
            <div v-if="showDeleteInput" class="space-y-6">
              <div>
                <label for="deletePassword" class="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span class="material-symbols-outlined text-gray-400">lock</span>
                  </div>
                  <input
                    id="deletePassword"
                    v-model="deletePassword"
                    :type="showPassword ? 'text' : 'password'"
                    placeholder="Enter your password"
                    required
                    :disabled="loading"
                    class="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-red-600 focus:border-red-600 placeholder-gray-400 transition duration-200 disabled:opacity-50"
                  />
                  <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      @click="showPassword = !showPassword"
                      class="text-gray-400 hover:text-gray-500 focus:outline-none"
                      :disabled="loading"
                    >
                      <span class="material-symbols-outlined text-xl">
                        {{ showPassword ? 'visibility_off' : 'visibility' }}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              <div class="flex space-x-4">
                <button
                  @click="confirmDeleteAccount"
                  :disabled="loading || !deletePassword"
                  class="flex-1 flex justify-center items-center py-3 px-4 rounded-lg font-semibold text-white bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg v-if="loading" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>{{ loading ? "Deleting..." : "Confirm Delete" }}</span>
                </button>

                <button
                  @click="cancelDelete"
                  :disabled="loading"
                  class="flex-1 flex justify-center items-center py-3 px-4 rounded-lg font-semibold text-blue-900 border border-blue-900 bg-white hover:bg-blue-50 transition disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>

              <p class="text-xs text-red-600 text-center mt-4">
                Warning: This action is irreversible. All your data will be permanently deleted.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { deleteAccount } from "@/composables/useAuth";
import AppHeaderBack from '@/components/common/AppHeaderBack.vue';

export default {
  components: {
    AppHeaderBack,
  },
  data() {
    return {
      showDeleteInput: false,
      deletePassword: "",
      showPassword: false,
      loading: false,
    };
  },
  methods: {
    async confirmDeleteAccount() {
      if (!this.deletePassword) {
        this.$store.dispatch("showAlert", { 
          message: "Please enter your password to delete your account.", 
          type: "error" 
        });
        return;
      }

      this.loading = true;

      try {
        await deleteAccount(this.deletePassword);
        this.$store.dispatch("showAlert", { 
          message: "Account deleted successfully.", 
          type: "success" 
        });
        this.$router.push("/register");
      } catch (error) {
        this.$store.dispatch("showAlert", { 
          message: error.message == "Firebase: Error (auth/invalid-credential)." ? 'Incorrect password' : error.message,
          type: "error" 
        });
      } finally {
        this.deletePassword = "";
        this.showDeleteInput = false;
        this.loading = false;
      }
    },
    cancelDelete() {
      this.deletePassword = "";
      this.showDeleteInput = false;
    },
  },
};
</script>

<!-- No custom CSS needed, all styling is handled by Tailwind classes -->